import React from 'react';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <StickyHeader />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            Terms & Conditions
          </h1>
          <p className="text-slate-500 text-sm">
            Last updated: January 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <div className="prose prose-slate max-w-none">
            
            {/* Intro */}
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              These terms govern your use of ComplyKit's services. By using our platform, 
              you agree to these terms. We've kept them clear and fair.
            </p>

            {/* General Terms */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6 pb-2 border-b border-slate-200">
                General Terms
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Scope of services</h3>
                  <p className="text-slate-600 mb-3">
                    ComplyKit provides compliance assistance services including but not limited to:
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Business registrations (company, LLP, partnership, etc.)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Tax filings (GST, income tax, TDS)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Regulatory compliance (MCA filings, annual returns)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Intellectual property (trademark, patent, copyright)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Document preparation and advisory services</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Our role</h3>
                  <p className="text-slate-600">
                    ComplyKit acts as a service facilitator. We connect you with qualified professionals 
                    (Chartered Accountants, Company Secretaries, lawyers) and provide technology to 
                    streamline compliance processes. We prepare and submit documents on your behalf 
                    through the appropriate government portals.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Third-party dependencies</h3>
                  <p className="text-slate-600">
                    Many services depend on government portals (MCA, GST Network, Income Tax portal) 
                    and regulatory bodies. Processing times, approvals, and outcomes are subject to 
                    these external systems and are outside our direct control. We will keep you 
                    informed of any delays or issues.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Your responsibilities</h3>
                  <p className="text-slate-600 mb-3">
                    To ensure successful service delivery, you agree to:
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Provide accurate and complete information</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Submit required documents in a timely manner</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Respond to queries and requests promptly</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Review and approve documents before final submission</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                      <span>Inform us of any changes that may affect your filings</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Payments & Pricing */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6 pb-2 border-b border-slate-200">
                Payments & Pricing
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Service fees</h3>
                  <p className="text-slate-600">
                    Prices displayed on our platform are professional service fees charged by 
                    ComplyKit for facilitating compliance services. These fees cover document 
                    preparation, expert review, portal submissions, and ongoing support.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Government fees</h3>
                  <p className="text-slate-600">
                    Government fees, stamp duties, and statutory charges are billed separately 
                    at actuals. These are passed through without markup and will be clearly 
                    communicated before payment.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Add-ons</h3>
                  <p className="text-slate-600">
                    Optional add-on services are clearly disclosed and priced separately. 
                    You will never be charged for services you didn't explicitly request.
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong className="text-slate-900">Note:</strong> All prices are in Indian Rupees (INR) 
                    and exclusive of GST unless otherwise stated.
                  </p>
                </div>
              </div>
            </section>

            {/* Refund Policy */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6 pb-2 border-b border-slate-200">
                Refund & Cancellation Policy
              </h2>
              
              <div className="space-y-6">
                <p className="text-slate-600">
                  We aim to be fair and transparent about refunds. Here's how it works:
                </p>

                <div className="space-y-4">
                  <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">Full refund</h4>
                    <p className="text-sm text-green-800">
                      If work has not yet started on your request, you're eligible for a full refund 
                      of the service fee. Simply contact us to cancel.
                    </p>
                  </div>

                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2">Partial refund</h4>
                    <p className="text-sm text-amber-800">
                      If work has started but is not yet complete (documents being prepared, 
                      applications being drafted), you may be eligible for a partial refund. 
                      The refund amount will depend on the work completed.
                    </p>
                  </div>

                  <div className="p-4 border border-slate-200 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">No refund</h4>
                    <p className="text-sm text-slate-600">
                      Once filings or submissions have been made to government portals, 
                      no refund is possible as the work is complete.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Government fees</h3>
                  <p className="text-slate-600">
                    Government fees, once paid to the respective authority, are non-refundable. 
                    This is standard practice as these payments are made directly to government bodies.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Refund timeline</h3>
                  <p className="text-slate-600">
                    Approved refunds are processed within 7–10 working days. The amount will be 
                    credited to your original payment method.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">How to request a refund</h3>
                  <p className="text-slate-600">
                    Contact us at{' '}
                    <a href="mailto:support@complykit.in" className="text-slate-900 font-medium hover:underline">
                      support@complykit.in
                    </a>
                    {' '}with your order details. We'll review your request and respond within 
                    2 working days.
                  </p>
                </div>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6 pb-2 border-b border-slate-200">
                Limitation of Liability
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">No guarantee of outcomes</h3>
                  <p className="text-slate-600">
                    While we work diligently to prepare accurate filings and applications, 
                    final approvals are at the discretion of government authorities. We cannot 
                    guarantee approval of registrations, acceptance of filings, or specific 
                    processing times by regulatory bodies.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Liability cap</h3>
                  <p className="text-slate-600">
                    Our maximum liability for any claim arising from our services is limited to 
                    the service fees you paid for that specific service. This does not apply to 
                    cases of gross negligence or willful misconduct.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Information accuracy</h3>
                  <p className="text-slate-600">
                    We rely on the information you provide. We are not liable for errors or 
                    delays resulting from inaccurate, incomplete, or late information from you.
                  </p>
                </div>
              </div>
            </section>

            {/* Governing Law */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-slate-900 mb-6 pb-2 border-b border-slate-200">
                Governing Law
              </h2>
              <p className="text-slate-600">
                These terms are governed by the laws of India. Any disputes shall be subject 
                to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.
              </p>
            </section>

            {/* Contact */}
            <section className="p-6 bg-slate-900 rounded-xl text-white">
              <h2 className="text-xl font-semibold mb-3">Questions about these terms?</h2>
              <p className="text-slate-300 mb-4">
                If you have questions about these terms or need clarification, we're here to help:
              </p>
              <a 
                href="mailto:support@complykit.in" 
                className="text-white font-medium hover:underline"
              >
                support@complykit.in
              </a>
            </section>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
